import React from 'react';
import {FormProvider, useForm, useFormContext} from 'react-hook-form';
import type {ProyectoFormDTO} from '../../../../api/Objetos';
import {seguimientoCoffeApi} from '../../../../api/api';
import {createEmptyProyecto} from '../emptyProyecto';
import {JsonTableField, ToggleField} from '../../../../ui/form/fields';
import {FormSection, useProjectReadOnly} from '../projectForm.helpers';
import {SectionAlerts, SectionSaveButton} from '../sectionCrudUtils';
import {useRegisterSectionSave} from '../SectionSaveCoordinator';
import {useQuery} from "@tanstack/react-query";
import BarraCargando from "../../../../ui/BarraCargando";
import { useTranslation } from 'react-i18next';
import {DocumentosAdjuntosPanel} from '../DocumentosAdjuntosPanel';

function SeguimientoCoffeSectionFields(props: { proyectoId: number; projectSlug: string; showIds?: boolean, isLoading: boolean, loadError: any }) {
    const { t } = useTranslation();
    const form = useFormContext<ProyectoFormDTO>();
    const readOnly = useProjectReadOnly();
    return (
        <FormSection
            id="seguimiento-coffe"
            title={t('seguimientoCoffeSection.title')}
            collapsible
            defaultOpen={false}
            headerActions={!readOnly &&
                <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                    {props.isLoading && <BarraCargando size="1.2em" />}
                    <ToggleField
                        label={t('seguimientoCoffeSection.public')}
                        name="seguimientoCoFFE.publico"
                        register={form.register}
                    />
                </div>
            }
        >
            <SectionAlerts loadError={props.loadError}/>
            <JsonTableField
                label={t('seguimientoCoffeSection.trackingTable')}
                name="seguimientoCoFFE.datosVariables"
                control={form.control}
                projectId={props.proyectoId}
                projectSlug={props.projectSlug}
                slug="seguimiento-coffe-datos-variables"
                previewMode="link"
            />
            <div className="adminNestedPanel">
                <h3>Captura de Seguimiento CoFFEE</h3>
                <DocumentosAdjuntosPanel proyectoId={props.proyectoId} ambito="SEGUIMIENTO_COFFEE"
                    accept="image/png,image/jpeg,image/webp" maxFiles={1} imagePreview compact/>
            </div>
        </FormSection>
    );
}

export function SeguimientoCoffeSection(props: { proyectoId: number; projectSlug: string; showIds?: boolean }) {
    const readOnly = useProjectReadOnly();
    const form = useForm<ProyectoFormDTO>({defaultValues: createEmptyProyecto()});

    const { data: coffeData, refetch, isPending, error } = useQuery({
        queryKey: ['admin', 'proyectos', 'seguimiento-coffe', props.proyectoId],
        queryFn: () => seguimientoCoffeApi.get(props.proyectoId),
    });

    React.useEffect(() => {
        if (coffeData) {
            form.reset({ ...form.getValues(), seguimientoCoFFE: coffeData });
        }
    }, [coffeData, form]);

    const handleSave = form.handleSubmit(async (value) => {
        if (value.seguimientoCoFFE) {
            await seguimientoCoffeApi.save(props.proyectoId, value.seguimientoCoFFE);
            await refetch();
        }
    });

    useRegisterSectionSave({
        id: 'seguimiento-coffe',
        isDirty: form.formState.isDirty,
        isSaving: isPending,
        save: async () => {
            await handleSave();
        },
    });

    return (
        <FormProvider {...form}>
            <SeguimientoCoffeSectionFields 
                proyectoId={props.proyectoId} 
                projectSlug={props.projectSlug}
                showIds={props.showIds}
                isLoading={isPending}
                loadError={error}
            />
            {!readOnly && <SectionSaveButton onClick={() => void handleSave()} busy={isPending} />}
        </FormProvider>
    );
}
